import { Badge, Box, FlexBox } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { textualChannels } from 'lib-shared-types-channels';
import { memo, useContext, useMemo } from 'react';

import { ConversationContext } from 'components/conversation';
import { ConversationCopilotDropdownTranslate } from 'components/conversation/conversationCopilotDropdown/components/translate/conversationCopilotDropdownTranslate';
import {
  CopilotDropdown,
  CopilotDropdownActionParams,
} from 'components/copilotDropdown';
import { useCopilotTrial } from 'utils/useCopilotTrial';

/**
 * Copilot dropdown for the conversation preview modal. Shows only the translate
 * action (summary is always visible in the preview).
 */
export const ConversationPreviewCopilotDropdown = memo(
  function ConversationPreviewCopilotDropdown(): JSX.Element | null {
    const { hasCopilot } = useCopilotTrial();
    const { conversation, isAutoTranslating, translationLanguageCode } =
      useContext(ConversationContext);

    const actions = useMemo(
      () => ({
        translate: {
          title: () => (
            <FlexBox>
              {translationLanguageCode
                ? 'Change translation language'
                : 'Translate'}

              {isAutoTranslating && (
                <Box margin="0 0 0 200">
                  <Badge variant="success" size="small">
                    Auto
                  </Badge>
                </Box>
              )}
            </FlexBox>
          ),
          icon: 'translate' as const,
          isVisible: ({ hasCopilot }: CopilotDropdownActionParams) =>
            hasCopilot,
          isDisabled: (_: CopilotDropdownActionParams): boolean =>
            !textualChannels.some(
              (channel) =>
                channel ===
                convertChannelCasing(
                  conversation?.initial_channel ?? '',
                  'snake',
                ),
            ),
          content: <ConversationCopilotDropdownTranslate />,
        },
      }),
      [
        conversation?.initial_channel,
        isAutoTranslating,
        translationLanguageCode,
      ],
    );

    if (!hasCopilot) {
      return null;
    }

    return <CopilotDropdown icon="ai" fixed actions={actions} />;
  },
);
